// src/hooks/usePermissionsSettingsData.js
import {
    useCallback,
    useEffect,
    useState,
  } from "react";
  
  import {
    getSocket,
  } from "../lib/socket";
  
  import {
    mergePermissionsMatrix,
    prefetchPermissionsSettingsData,
    readPermissionsSettingsCache,
    subscribePermissionsSettingsCache,
    writePermissionsSettingsCache,
    patchPermissionsRoleCache,
  } from "../utils/permissionsSettingsCache";
  
  
  let socketConsumerCount =
    0;
  
  let sharedSocket =
    null;
  
  let hasConnectedOnce =
    false;
  
  
  /* -------------------------------------------------------------------------- */
  /* WebSocket                                                                  */
  /* -------------------------------------------------------------------------- */
  
  const subscribeSocket =
    () => {
      sharedSocket?.emit(
        "subscribe:permissions"
      );
    };
  
  
  const refreshAfterReconnect =
    () => {
      prefetchPermissionsSettingsData({
        force: true,
      }).catch(
        (
          error
        ) => {
          console.warn(
            "[usePermissionsSettingsData] reconnect refresh failed:",
            error
          );
        }
      );
    };
  
  
  const onSocketConnect =
    () => {
      subscribeSocket();
  
      if (
        hasConnectedOnce
      ) {
        refreshAfterReconnect();
      }
  
      hasConnectedOnce =
        true;
    };
  
  
  const onMatrixUpdated =
    (
      payload
    ) => {
      if (
        !payload?.matrix
      ) {
        return;
      }
  
      const current =
        readPermissionsSettingsCache();
  
      /*
       * Merge instead of blindly replacing.
       *
       * This is important in case the backend sends only one role
       * in the websocket payload.
       */
      const nextMatrix =
        mergePermissionsMatrix(
          current?.matrix,
          payload.matrix
        );
  
      writePermissionsSettingsCache(
        nextMatrix,
        {
          fetchedAt:
            Date.now(),
        }
      );
    };
  
  
  const startSocket =
    () => {
      if (
        sharedSocket
      ) {
        return;
      }
  
      sharedSocket =
        getSocket();
  
      sharedSocket.on(
        "connect",
        onSocketConnect
      );
  
      sharedSocket.on(
        "reconnect",
        onSocketConnect
      );
  
      sharedSocket.on(
        "permissions:matrixUpdated",
        onMatrixUpdated
      );
  
      /*
       * The socket may already be connected before this hook mounts.
       */
      subscribeSocket();
    };
  
  
  const stopSocket =
    () => {
      if (
        !sharedSocket
      ) {
        return;
      }
  
      sharedSocket.emit(
        "unsubscribe:permissions"
      );
  
      sharedSocket.off(
        "connect",
        onSocketConnect
      );
  
      sharedSocket.off(
        "reconnect",
        onSocketConnect
      );
  
      sharedSocket.off(
        "permissions:matrixUpdated",
        onMatrixUpdated
      );
  
      sharedSocket =
        null;
  
      hasConnectedOnce =
        false;
    };
  
  
  const registerSocketConsumer =
    () => {
      socketConsumerCount +=
        1;
  
      startSocket();
  
      return () => {
        socketConsumerCount =
          Math.max(
            0,
            socketConsumerCount -
              1
          );
  
        if (
          socketConsumerCount ===
          0
        ) {
          stopSocket();
        }
      };
    };
  
  
  /* -------------------------------------------------------------------------- */
  /* Hook                                                                       */
  /* -------------------------------------------------------------------------- */
  
  export function usePermissionsSettingsData() {
    const [
      data,
      setData,
    ] =
      useState(
        () =>
          readPermissionsSettingsCache()
      );
  
    const [
      error,
      setError,
    ] =
      useState("");
  
  
    useEffect(() => {
      let alive =
        true;
  
      const unsubscribe =
        subscribePermissionsSettingsCache(
          (
            nextData
          ) => {
            if (!alive) {
              return;
            }
  
            setData(
              nextData ||
                null
            );
  
            setError(
              ""
            );
          }
        );
  
  
      prefetchPermissionsSettingsData()
        .then(
          (
            nextData
          ) => {
            if (
              alive &&
              nextData
            ) {
              setData(
                nextData
              );
            }
          }
        )
        .catch(
          (
            loadError
          ) => {
            if (!alive) {
              return;
            }
  
            console.warn(
              "[usePermissionsSettingsData] load failed:",
              loadError
            );
  
            if (
              !readPermissionsSettingsCache()
            ) {
              setError(
                "load_failed"
              );
            }
          }
        );
  
  
      return () => {
        alive =
          false;
  
        unsubscribe();
      };
    }, []);
  
  
    useEffect(
      () =>
        registerSocketConsumer(),
      []
    );
  
  
    const refresh =
      useCallback(
        () =>
          prefetchPermissionsSettingsData({
            force: true,
          }),
        []
      );
  
  
    const patchRole =
      useCallback(
        (
          role,
          updater
        ) =>
          patchPermissionsRoleCache(
            role,
            updater
          ),
        []
      );
  
  
    return {
      matrix:
        data?.matrix ||
        null,
  
      loading:
        !data?.matrix,
  
      error,
  
      refresh,
      patchRole,
  
      hasCachedData:
        Boolean(
          data?.matrix
        ),
    };
  }